import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, Clock, MapPin, Phone, RotateCcw, ShoppingBag, X
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';

const money = value => `£${Number(value || 0).toFixed(2)}`;

const getSelectedOptionNames = item => Object.values(item?.selectedOptions || {})
  .flatMap(value => Array.isArray(value) ? value : [value])
  .map(choice => typeof choice === 'string' ? choice : choice?.name)
  .filter(Boolean);

const parseUtcDate = value => {
  if (!value) return null;
  const raw = String(value);
  const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLondonTime = date => date.toLocaleTimeString('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

export default function OrderStatusPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentOrder, clearCart, branding } = useApp();
  const [paymentStatus, setPaymentStatus] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restaurantSettings, setRestaurantSettings] = useState(null);

  useEffect(() => {
    let tag = document.querySelector('meta[name="robots"]');
    const created = !tag;
    const previous = tag?.getAttribute('content');

    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      document.head.appendChild(tag);
    }

    tag.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (created) tag.remove();
      else if (previous) tag.setAttribute('content', previous);
      else tag.removeAttribute('content');
    };
  }, []);

  useEffect(() => {
    document.title = restaurantSettings?.name
      ? `Order Status - ${restaurantSettings.name}`
      : 'Order Status';
  }, [restaurantSettings]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setRestaurantSettings(await api.getRestaurant());
      } catch (error) {
        console.error('Failed to fetch restaurant settings:', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchOrder = async () => {
      try {
        const fetchedOrder = await api.getOrder(orderId);
        if (mounted) setOrder(fetchedOrder);
      } catch (error) {
        console.error('Failed to fetch order:', error);
        if (mounted) {
          setOrder(currentOrder || {
            id: orderId,
            type: 'delivery',
            total: 0,
            items: [],
            status: 'pending'
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchOrder();
    const interval = setInterval(fetchOrder, 5000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [orderId, currentOrder]);

  useEffect(() => {
    const payment = searchParams.get('payment');

    if (payment === 'success') {
      setPaymentStatus('processing');
      window.history.replaceState({}, '', `/order-status/${orderId}`);
      clearCart();
      localStorage.removeItem('checkoutFormData');
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
      window.history.replaceState({}, '', `/order-status/${orderId}`);
    }
  }, []);

  useEffect(() => {
    if (order?.paymentStatus === 'paid') {
      setPaymentStatus(current => current === 'processing' ? 'success' : current);
    }
  }, [order?.paymentStatus]);

  if (loading || !order) {
    return (
      <div className="min-h-screen bg-[#f7f7f5] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const orderNumber = order.orderNumber || order.order_number || '—';
  const shortId = order.id?.slice(-8)?.toUpperCase();
  const restaurantName = restaurantSettings?.name || 'Your restaurant';
  const logoUrl = branding?.logoUrl || restaurantSettings?.logoUrl;
  const accentColor = restaurantSettings?.primaryColor || branding?.primaryColor || '#111827';
  const isPickup = order.type === 'pickup';
  const email = order.customerEmail || order.email || order.userEmail;

  const pointsEarned = (() => {
    if (!restaurantSettings?.rewardsEnabled) return 0;
    const rate = restaurantSettings.pointsPerPound || 10;
    const spend = Math.max(0, Number(order.subtotal || 0) - Number(order.discount || 0));
    return Math.floor(spend * rate);
  })();

  const steps = isPickup
    ? [
        { key: 'awaiting_acceptance', label: 'Received' },
        { key: 'accepted', label: 'Accepted' },
        { key: 'preparing', label: 'Preparing' },
        { key: 'ready', label: order.status === 'completed' ? 'Completed' : 'Ready' }
      ]
    : [
        { key: 'awaiting_acceptance', label: 'Received' },
        { key: 'accepted', label: 'Accepted' },
        { key: 'preparing', label: 'Preparing' },
        { key: 'out_for_delivery', label: 'On the way' },
        { key: 'delivered', label: order.status === 'completed' ? 'Completed' : 'Delivered' }
      ];

  const statusMap = {
    pending: 0,
    awaiting_acceptance: 0,
    accepted: 1,
    preparing: 2,
    ready: 3,
    out_for_delivery: 3,
    delivered: 4,
    completed: steps.length - 1
  };
  const currentStep = Math.min(statusMap[order.status] ?? 0, steps.length - 1);

  const statusCopy = {
    pending: ['Awaiting acceptance', 'The restaurant has received your order.'],
    awaiting_acceptance: ['Awaiting acceptance', 'The restaurant has received your order.'],
    accepted: ['Order accepted', 'Your order has been accepted and is in progress.'],
    preparing: ['Preparing your order', 'Your food is being freshly prepared.'],
    ready: ['Ready for collection', 'Your order is ready at the counter.'],
    out_for_delivery: ['On the way', 'Your order is out for delivery.'],
    delivered: ['Delivered', 'Your order has been delivered. Enjoy!'],
    completed: ['Order completed', 'Thank you for ordering with us.']
  };
  const [statusTitle, statusDescription] = statusCopy[order.status] || statusCopy.pending;

  const estimate = (() => {
    if (['pending', 'awaiting_acceptance'].includes(order.status)) return null;
    if (order.scheduledFor && !['asap', 'ASAP'].includes(order.scheduledFor)) {
      return {
        label: isPickup ? 'Scheduled collection' : 'Scheduled delivery',
        value: order.scheduledFor,
        note: 'Your order is scheduled for this time'
      };
    }

    const orderTime = parseUtcDate(order.createdAt);
    if (!orderTime) return null;
    const fallback = isPickup ? '10-20' : '30-45';
    const leadTime = isPickup
      ? restaurantSettings?.pickupTime || fallback
      : restaurantSettings?.deliveryTime || fallback;
    const values = String(leadTime).match(/\d+/g)?.map(Number) || [];
    if (!values.length) return null;

    const min = values[0];
    const max = values[1] ?? values[0];
    const start = new Date(orderTime.getTime() + min * 60000);
    const end = new Date(orderTime.getTime() + max * 60000);
    return {
      label: isPickup ? 'Estimated collection' : 'Estimated delivery',
      value: `${formatLondonTime(start)}–${formatLondonTime(end)}`,
      note: isPickup ? 'Collect your order between these times' : 'Expected between these times'
    };
  })();

  const renderOrderSummary = () => (
    <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm shadow-gray-200/30">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-gray-900">Your order</p>
          <p className="text-xs text-gray-500 mt-0.5">{isPickup ? 'Collection' : 'Delivery'} · {order.paymentMethod || 'Payment'}</p>
        </div>
        <span className="text-sm font-semibold text-gray-900">#{orderNumber}</span>
      </div>

      <div className="px-5 py-4 space-y-3">
        {order.items?.map((item, index) => {
          const optionNames = getSelectedOptionNames(item);
          return (
            <div key={item.id || `${item.name}-${index}`} className="flex items-start justify-between gap-4 text-sm">
              <div className="min-w-0">
                <p className="text-gray-700 leading-5"><strong className="text-gray-900">{item.quantity}×</strong> {item.name}</p>
                {optionNames.length > 0 && <p className="mt-0.5 text-xs leading-5 text-gray-500">{optionNames.join(' · ')}</p>}
              </div>
              <span className="text-gray-900 whitespace-nowrap">{money(Number(item.price || 0) * Number(item.quantity || 0))}</span>
            </div>
          );
        })}

        <div className="border-t border-gray-100 pt-3 space-y-2 text-sm">
          <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
          {Number(order.deliveryFee || 0) > 0 && <div className="flex justify-between text-gray-500"><span>Delivery</span><span>{money(order.deliveryFee)}</span></div>}
          {Number(order.serviceCharge || 0) > 0 && <div className="flex justify-between text-gray-500"><span>Service charge</span><span>{money(order.serviceCharge)}</span></div>}
          {Number(order.discount || 0) > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>−{money(order.discount)}</span></div>}
          <div className="flex justify-between border-t border-gray-100 pt-3 text-base font-semibold text-gray-900"><span>Total</span><span>{money(order.total)}</span></div>
        </div>
      </div>
    </section>
  );

  const renderContact = () => (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm shadow-gray-200/30">
      <p className="font-semibold text-gray-900 mb-3">Restaurant details</p>
      <div className="space-y-2.5 text-sm text-gray-600">
        {restaurantSettings?.phone && (
          <a href={`tel:${restaurantSettings.phone}`} className="flex items-center gap-3 hover:text-gray-900">
            <Phone className="w-4 h-4 flex-none" style={{ color: accentColor }} />
            <span>{restaurantSettings.phone}</span>
          </a>
        )}
        {restaurantSettings?.address && (
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 flex-none mt-0.5" style={{ color: accentColor }} />
            <span>{restaurantSettings.address}</span>
          </div>
        )}
      </div>
    </section>
  );

  const isRefunded = order.paymentStatus === 'refunded';
  const terminal = isRefunded || ['rejected', 'expired', 'cancelled'].includes(order.status);

  const terminalDetails = (() => {
    if (isRefunded) return {
      title: 'Order refunded',
      description: `Your ${money(order.total)} card payment has been returned to you.`,
      note: 'Your bank may take several working days to display the returned funds.',
      icon: RotateCcw,
      iconClass: 'bg-violet-100 text-violet-700',
      badgeClass: 'bg-violet-50 text-violet-700'
    };
    if (order.status === 'expired') return {
      title: 'Checkout not completed',
      description: 'Payment was not completed in time and your order was not placed.',
      note: 'No payment was processed. You can return to the menu and try again.',
      icon: Clock,
      iconClass: 'bg-amber-100 text-amber-700',
      badgeClass: 'bg-amber-50 text-amber-700'
    };
    if (order.status === 'cancelled') return {
      title: 'Order cancelled',
      description: 'This order has been cancelled. No payment was processed.',
      note: 'You can return to the menu whenever you are ready to place a new order.',
      icon: X,
      iconClass: 'bg-gray-200 text-gray-700',
      badgeClass: 'bg-gray-100 text-gray-700'
    };
    return {
      title: 'Order rejected',
      description: "We're sorry, but the restaurant could not accept this order.",
      note: 'Please contact the restaurant if you need more information.',
      icon: X,
      iconClass: 'bg-red-100 text-red-700',
      badgeClass: 'bg-red-50 text-red-700'
    };
  })();

  const Header = () => (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-none">
          {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-contain p-1.5" /> : <ShoppingBag className="w-5 h-5 text-gray-700" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">{restaurantName}</p>
          <p className="text-xs text-gray-500">Order #{orderNumber}{shortId ? ` · ${shortId}` : ''}</p>
        </div>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-3 py-1.5">Order status</span>
      </div>
    </header>
  );

  if (terminal) {
    const TerminalIcon = terminalDetails.icon;
    return (
      <div className="min-h-screen bg-[#f7f7f5] font-sans text-gray-900">
        <Header />
        <main className="max-w-xl mx-auto px-4 py-5 sm:py-8 space-y-4">
          <section className="bg-white border border-gray-200 rounded-2xl p-6 text-center shadow-sm shadow-gray-200/30">
            <div className={`w-14 h-14 rounded-full mx-auto flex items-center justify-center ${terminalDetails.iconClass}`}>
              <TerminalIcon className="w-6 h-6" />
            </div>
            <span className={`inline-flex mt-4 px-3 py-1 rounded-full text-xs font-semibold ${terminalDetails.badgeClass}`}>{terminalDetails.title}</span>
            <h1 className="mt-4 text-xl sm:text-2xl font-semibold tracking-tight">{terminalDetails.description}</h1>
            <p className="mt-3 text-sm leading-6 text-gray-500 max-w-md mx-auto">{terminalDetails.note}</p>
          </section>

          {renderOrderSummary()}
          {renderContact()}

          <button onClick={() => navigate('/')} className="w-full h-12 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors">
            Back to menu
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f5] font-sans text-gray-900">
      <Header />
      <main className="max-w-xl mx-auto px-4 py-5 sm:py-8 space-y-4">
        {paymentStatus === 'processing' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 flex-none mt-0.5" />
            <div><p className="text-sm font-semibold text-blue-900">Confirming your payment</p><p className="text-xs text-blue-700 mt-1">This normally takes only a few seconds.</p></div>
          </div>
        )}
        {paymentStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-none mt-0.5" />
            <div><p className="text-sm font-semibold text-green-900">Payment successful</p><p className="text-xs text-green-700 mt-1">Your payment has been processed securely.</p></div>
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-red-900">Payment cancelled</p>
            <p className="text-xs text-red-700 mt-1">You cancelled the payment. The order status will update automatically.</p>
          </div>
        )}

        <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm shadow-gray-200/30">
          <div className="p-5 sm:p-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">{statusTitle}</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">{statusDescription}</p>
            </div>

            {estimate && (
              <div className="mt-5 rounded-xl bg-gray-50 border border-gray-100 p-4">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500"><Clock className="w-4 h-4" /><span>{estimate.label}</span></div>
                <p className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900">{estimate.value}</p>
                <p className="mt-1 text-xs text-gray-500">{estimate.note}</p>
              </div>
            )}

            <div className="mt-5">
              <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
                {steps.map((step, index) => (
                  <div key={step.key} className="h-1.5 rounded-full" style={{ backgroundColor: index <= currentStep ? accentColor : '#e5e7eb' }} />
                ))}
              </div>
              <div className="mt-3 text-xs">
                <span className="font-semibold text-gray-800">{steps[currentStep]?.label}</span>
              </div>
            </div>
          </div>
        </section>

        {pointsEarned > 0 && (
          <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm shadow-gray-200/30 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-xl flex-none">🎉</div>
            <div>
              <p className="font-semibold text-gray-900">{pointsEarned} points earned</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">{email ? `Added to ${email}. ` : ''}Use your points for discounts on a future order.</p>
            </div>
          </section>
        )}

        {renderOrderSummary()}
        {renderContact()}

        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate('/')} className="h-12 rounded-xl border border-gray-200 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50 transition-colors">Back to menu</button>
          <button onClick={() => navigate('/account')} className="h-12 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors">View orders</button>
        </div>
      </main>
    </div>
  );
}
